import {flatten} from 'lodash';
import React from 'react';

import {
  AttachmentModel,
  attachmentPreviewableImageTypes,
  AttachmentVisualContentTypesEnum,
  isAttachmentModel,
} from '../../libs/shared/core/src/models/api/attachmentModel';
import {TeammateModel} from '../../libs/shared/core/src/models/api/teammateModel';
import {ViewerPanelId} from '../../libs/shared/core/src/store/panels/panelStateBase';
import {CommentReactionVisualProps} from '../../srcCommon/inboxes/viewer/activity/comment/commentViewerSelectors';
import {renderSentReference} from '../components/inboxes/viewer/activity/basic/referencedActivityTextHelpers';
import {TeammateNamePreviewer} from '../components/teammateCard/teammateCardPreviewer';
import {CommentModel} from '../models/api/commentModel';

export function renderCommentTitle(
  author: TeammateModel,
  referencedAuthor: TeammateModel | undefined,
  panelId: ViewerPanelId,
  userId?: number,
  isEditing?: boolean,
) {
  if (referencedAuthor && userId && !isEditing) {
    return renderSentReference(author, referencedAuthor, userId, panelId);
  }

  return <TeammateNamePreviewer teammate={author} panelId={panelId} shouldDisplayFullName />;
}

export function shouldUseMultiAttachmentStyle(comment: CommentModel) {
  const {text, attachments} = comment;

  if (attachments.length > 1) {
    return true;
  }

  if (attachments.length === 1 && text.trim()) {
    return true;
  }

  return false;
}

/** Find the single image attached to this comment, if any. */
export function findSingleImageAttachment(comment: CommentModel): AttachmentModel | undefined {
  const {text, attachments} = comment;

  // This only applies to empty comments with a single attachment.
  if (text.trim() || attachments.length !== 1) {
    return undefined;
  }

  // If so, extract it.
  const attachment = attachments[0];
  if (!isAttachmentModel(attachment) || attachment.size > 2000000) {
    return undefined;
  }

  // We're only looking for images.
  if (attachment.visualContentType !== AttachmentVisualContentTypesEnum.IMAGE) {
    return undefined;
  }

  // Make sure the browser supports the image file type.
  if (!attachmentPreviewableImageTypes.includes(attachment.contentType)) {
    return undefined;
  }

  return attachment;
}

export function hasUpdatedReactions(
  oldReactions: ReadonlyArray<CommentReactionVisualProps> | undefined,
  newReactions: ReadonlyArray<CommentReactionVisualProps> | undefined,
) {
  if (!oldReactions && newReactions) {
    return true;
  }

  if (!oldReactions || !newReactions) {
    return false;
  }

  if (oldReactions.length !== newReactions.length) {
    return true;
  }

  // Check the number of people reacting, if that has changed the visuals should change too.
  const oldReactionTeammates = flatten(oldReactions.map((reaction) => reaction.teammates));
  const newReactionTeammates = flatten(newReactions.map((reaction) => reaction.teammates));
  if (oldReactionTeammates.length !== newReactionTeammates.length) {
    return true;
  }

  return false;
}
